import {
  Bank,
  CaretDown,
  CaretLeft,
  CaretUp,
  House,
  MagnifyingGlass,
  MapPin,
  Money,
  SignOut,
  Storefront,
  Ticket,
  User,
  TruckTrailer,
  UsersThree,
  ChatCenteredDots,
  Envelope,
  Phone,
  PhoneOutgoing,
  Headset,
  ChatDots,
  Speedometer,
  Gift,
  CreditCard,
  Info,
  Gear,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerWithDriverOnboarding, Entity } from '../../types/apiTypes';
import { useScreenView } from '../../utils/ScreenViewContext';
import { useUser } from '../../utils/UserContext';
import { useBranding } from '../../providers/BrandingProvider';
import { SupportModal } from './customers/tabs/SupportModal';
import { ReferCarrierModal } from './ReferCarrierModal';
import { isPreActiveCustomer } from './customers/tabs/home/utils/demoData';

interface SideNavProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  showSideNav: boolean;
  setShowSideNav: (show: boolean) => void;
  uncategorizedTransactionsCount: number;
  isLoadingUncategorizedTransactionsCount: boolean;
}

const navLinks = [
  {
    name: 'Home',
    path: '/dashboard/home',
    icon: <House size={20} className="mr-2" />,
  },
  {
    name: 'Banking',
    path: '/dashboard/banking/overview',
    icon: <Money size={20} className="mr-2" />,
    subTabs: [
      { name: 'Overview', path: '/dashboard/banking/overview' },
      {
        name: 'Cards',
        path: '/dashboard/banking/cards',
      },
      {
        name: 'Transactions',
        path: '/dashboard/banking/transactions',
        notificationCount: 2,
      },
      {
        name: 'Cash Advances',
        path: '/dashboard/banking/cash-advances/active',
      },

      {
        name: 'Statements',
        path: '/dashboard/banking/statements',
      },
    ],
  },
  {
    name: 'Finances',
    path: '/dashboard/finances/overview',
    icon: <Bank size={20} className="mr-2" />,
    subTabs: [
      { name: 'Overview', path: '/dashboard/finances/overview' },
      { name: 'Budgeting', path: '/dashboard/finances/budgeting' },
      {
        name: 'Driver Performance',
        path: '/dashboard/finances/driver-performance',
      },
      {
        name: 'Data Exports',
        path: '/dashboard/finances/data-exports',
      },
      {
        name: 'Accounting',
        path: '/dashboard/finances/accounting',
      },
    ],
  },
  {
    name: 'Vendor Marketplace',
    path: '/dashboard/vendor-marketplace',
    icon: <Storefront size={20} className="mr-2" />,
  },
  {
    name: 'Assets',
    path: '/dashboard/assets',
    icon: <TruckTrailer size={20} className="mr-2" />,
  },
  {
    name: 'Drivers',
    path: '/dashboard/drivers',
    icon: <UsersThree size={20} className="mr-2" />,
  },
];

const driverNavLinks: {
  name: string;
  path: string;
  icon: ReactNode;
  subTabs?: { name: string; path: string; notificationCount?: number }[];
}[] = [
  {
    name: 'Vendor Marketplace',
    path: '/dashboard/vendor-marketplace',
    icon: <Storefront size={20} className="mr-2" />,
  },
  {
    name: 'Submit Mileage',
    path: '/dashboard/submit-mileage-drivers',
    icon: <Speedometer size={20} className="mr-2" />,
  },
  {
    name: 'Cards',
    path: `/dashboard/cards-drivers`,
    icon: <CreditCard size={20} className="mr-2" />,
  },
];

const vendorNavLinks: {
  name: string;
  path: string;
  icon: ReactNode;
  subTabs?: { name: string; path: string; notificationCount?: number }[];
}[] = [
  {
    name: 'Home',
    path: '/dashboard/home',
    icon: <House size={20} className="mr-2" />,
  },
  {
    name: 'Referrals',
    path: '/dashboard/referrals',
    icon: <UsersThree size={20} className="mr-2" />,
  },
  {
    name: 'Locations',
    path: '/dashboard/locations',
    icon: <MapPin size={20} className="mr-2" />,
  },
  {
    name: 'Promotions',
    path: '/dashboard/promotions',
    icon: <Ticket size={20} className="mr-2" />,
  },
  {
    name: 'Carrier Lookup',
    path: '/dashboard/carrier-lookup',
    icon: <MagnifyingGlass size={20} className="mr-2" />,
  },
];

const partnerNavLinks: {
  name: string;
  path: string;
  icon: ReactNode;
  subTabs?: { name: string; path: string; notificationCount?: number }[];
}[] = [
  {
    name: 'Referrals',
    path: '/dashboard/referrals',
    icon: <UsersThree size={20} className="mr-2" />,
    subTabs: [
      { name: 'Overview', path: '/dashboard/referrals/overview' },
      { name: 'Track Invites', path: '/dashboard/referrals/track-invites' },
    ],
  },
  {
    name: 'Carrier Lookup',
    path: '/dashboard/carrier-lookup',
    icon: <MagnifyingGlass size={20} className="mr-2" />,
  },
  {
    name: 'Agents',
    path: '/dashboard/agents',
    icon: <Headset size={20} className="mr-2" />,
  },
];

const partnerAgentNavLinks: {
  name: string;
  path: string;
  icon: ReactNode;
  subTabs?: { name: string; path: string; notificationCount?: number }[];
}[] = [
  {
    name: 'Referrals',
    path: '/dashboard/referrals/overview',
    icon: <UsersThree size={20} className="mr-2" />,
  },
  {
    name: 'Carrier Lookup',
    path: '/dashboard/carrier-lookup',
    icon: <MagnifyingGlass size={20} className="mr-2" />,
  },
];

const SideNav = ({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  showSideNav,
  setShowSideNav,
  uncategorizedTransactionsCount,
  isLoadingUncategorizedTransactionsCount,
}: SideNavProps) => {
  const [expandedTab, setExpandedTab] = useState<string | null>(null);
  const { userRecord } = useUser();
  const { logoUrl } = useBranding();
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isReferCarrierModalOpen, setIsReferCarrierModalOpen] = useState(false);

  const [isSideNavVisible, setIsSideNavVisible] = useState(showSideNav);
  const [isAnimating, setIsAnimating] = useState(false);
  const sideNavRef = useRef<HTMLDivElement | null>(null);
  const { logout } = useUser();

  const [popupVisible, setPopupVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const togglePopup = () => {
    setPopupVisible(!popupVisible);
  };
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        const toggleArea = event.target as HTMLElement;
        if (!toggleArea.closest('.user-box-toggle')) {
          setPopupVisible(false);
        }
      }
    };

    if (popupVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupVisible]);

  useEffect(() => {
    if (subTab?.length > 0) {
      setExpandedTab(currentTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);
  // To manage the smooth transition
  useEffect(() => {
    if (showSideNav) {
      // If opening the SideNav
      setIsSideNavVisible(true); // Ensure it's rendered immediately
      setTimeout(() => {
        setIsAnimating(true); // Start the transition (slide-in)
      }, 0); // Trigger animation after it's mounted
    } else {
      // If closing the SideNav
      setIsAnimating(false); // Trigger the transition (slide-out)
      setTimeout(() => {
        setIsSideNavVisible(false); // After the transition, hide the component
      }, 300); // 300ms to match the transition duration
    }
  }, [showSideNav]);

  useEffect(() => {
    if (screenView !== 'desktop' && showSideNav) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          sideNavRef.current &&
          !sideNavRef.current.contains(event.target as Node)
        ) {
          setShowSideNav(false); // Close the SideNav if clicking outside
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      // Cleanup listener on component unmount
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSideNav, screenView, setShowSideNav]);

  const handleTabClick = (tab: any) => {
    setCurrentTab(tab.name);
    if (tab.subTabs) {
      setExpandedTab(expandedTab === tab.name ? null : tab.name); // Toggle expanded tab
      navigate(tab.path);
    } else {
      setExpandedTab(null);
      navigate(tab.path);
    }
  };

  const handleSubTabClick = (subTab: string, path: string) => {
    setSubTab(subTab);
    navigate(path);
  };

  const getEntityNavLinks = (userType: Entity) => {
    switch (userType) {
      case Entity.Customer:
        return navLinks;
      case Entity.Vendor:
        return vendorNavLinks;
      case Entity.Partner:
        return partnerNavLinks;
      case Entity.PartnerAgent:
        return partnerAgentNavLinks;
      case Entity.Driver:
        return driverNavLinks;
    }
  };

  const renderNavLinks = (userType: Entity) => {
    switch (userType) {
      case Entity.Customer:
        return (
          <div className="flex flex-col items-start gap-y-[12px]">
            <div className="flex w-full justify-start border-b-[1px] border-b-[#E6E6E6] p-[12px] pb-[24px]">
              <img
                alt={logoUrl ? 'Company logo' : 'tach-logo'}
                src={logoUrl ?? '/TachLogo.svg'}
                className={
                  logoUrl ? 'h-10 w-auto max-w-[220px] object-contain' : 'h-6'
                }
                onError={(e) => {
                  e.currentTarget.src = '/TachLogo.svg';
                }}
              />
            </div>
            <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
              {navLinks.map((link, idx) => (
                <div key={idx} className="w-full">
                  <p
                    key={idx}
                    onClick={() => {
                      handleTabClick(link);
                    }}
                    className={`${currentTab === link?.name && !link.subTabs ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen' : currentTab === link?.name && link.subTabs ? 'text-ctadarkgreen' : 'text-[#858897] hover:bg-gray-100'} flex flex-row justify-between rounded-[6px] p-3 hover:cursor-pointer`}
                  >
                    <span className="flex items-center">
                      {link?.icon}
                      {link?.name}
                    </span>
                    {link.subTabs &&
                      (expandedTab === link.name ? (
                        <CaretUp size={20} />
                      ) : (
                        <CaretDown size={20} />
                      ))}
                  </p>
                  {link.subTabs && expandedTab === link.name && (
                    <div className="mt-2 space-y-2">
                      {link.subTabs.map((sub, subIdx) => (
                        <h2
                          key={subIdx}
                          onClick={() => handleSubTabClick(sub.name, sub.path)}
                          className={`flex items-center justify-between rounded-lg p-2 pl-4 ${
                            subTab === sub.name
                              ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen'
                              : 'text-[#858897] hover:bg-gray-100'
                          } hover:cursor-pointer`}
                        >
                          <p className="ml-8">{sub.name}</p>
                          {/* Show notification badge for Transactions */}
                          {sub.name === 'Transactions' &&
                            uncategorizedTransactionsCount > 0 && (
                              <span className="ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                                {uncategorizedTransactionsCount}
                              </span>
                            )}
                        </h2>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        );
      case Entity.Vendor:
        return (
          <div className="flex flex-col items-start gap-y-[12px]">
            <div className="flex w-full justify-start border-b-[1px] border-b-[#E6E6E6] p-[12px] pb-[24px]">
              <img alt="tach-logo" src="/TachLogo.svg" className="h-6" />
            </div>
            <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
              {vendorNavLinks.map((link, idx) => (
                <div key={idx} className="w-full">
                  <p
                    key={idx}
                    onClick={() => {
                      handleTabClick(link);
                    }}
                    className={`${currentTab === link?.name && !link.subTabs ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen' : currentTab === link?.name && link.subTabs ? 'text-ctadarkgreen' : 'text-[#858897] hover:bg-gray-100'} flex flex-row justify-between rounded-[6px] p-3 hover:cursor-pointer`}
                  >
                    <span className="flex items-center">
                      {link?.icon}
                      {link?.name}
                    </span>
                    {link.subTabs &&
                      (expandedTab === link.name ? (
                        <CaretUp size={20} />
                      ) : (
                        <CaretDown size={20} />
                      ))}
                  </p>
                  {link.subTabs && expandedTab === link.name && (
                    <div className="mt-2 space-y-2">
                      {link.subTabs.map((sub, subIdx) => (
                        <p
                          key={subIdx}
                          onClick={() => handleSubTabClick(sub.name, sub.path)}
                          className={`flex items-center justify-between rounded-lg p-2 pl-4 ${
                            subTab === sub.name
                              ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen'
                              : 'text-[#858897] hover:bg-gray-100'
                          } hover:cursor-pointer`}
                        >
                          <h2 className="ml-8">{sub.name}</h2>
                          {/* Show notification badge for Transactions */}
                          {sub.notificationCount && (
                            <span className="ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                              {sub.notificationCount}
                            </span>
                          )}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        );
      case Entity.PartnerAgent:
        return (
          <div className="flex flex-col items-start gap-y-[12px]">
            <div className="flex w-full justify-start border-b-[1px] border-b-[#E6E6E6] p-[12px] pb-[24px]">
              <img alt="tach-logo" src="/TachLogo.svg" className="h-6" />
            </div>
            <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
              {partnerAgentNavLinks.map((link, idx) => (
                <div key={idx} className="w-full">
                  <p
                    key={idx}
                    onClick={() => {
                      handleTabClick(link);
                    }}
                    className={`${currentTab === link?.name && !link.subTabs ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen' : currentTab === link?.name && link.subTabs ? 'text-ctadarkgreen' : 'text-[#858897] hover:bg-gray-100'} flex flex-row justify-between rounded-[6px] p-3 hover:cursor-pointer`}
                  >
                    <span className="flex items-center">
                      {link?.icon}
                      {link?.name}
                    </span>
                    {link.subTabs &&
                      (expandedTab === link.name ? (
                        <CaretUp size={20} />
                      ) : (
                        <CaretDown size={20} />
                      ))}
                  </p>
                  {link.subTabs && expandedTab === link.name && (
                    <div className="mt-2 space-y-2">
                      {link.subTabs.map((sub, subIdx) => (
                        <p
                          key={subIdx}
                          onClick={() => handleSubTabClick(sub.name, sub.path)}
                          className={`flex items-center justify-between rounded-lg p-2 pl-4 ${
                            subTab === sub.name
                              ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen'
                              : 'text-[#858897] hover:bg-gray-100'
                          } hover:cursor-pointer`}
                        >
                          <h2 className="ml-8">{sub.name}</h2>
                          {/* Show notification badge for Transactions */}
                          {sub.notificationCount && (
                            <span className="ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                              {sub.notificationCount}
                            </span>
                          )}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        );
      case Entity.Partner:
        return (
          <div className="flex flex-col items-start gap-y-[12px]">
            <div className="flex w-full justify-start border-b-[1px] border-b-[#E6E6E6] p-[12px] pb-[24px]">
              <img alt="tach-logo" src="/TachLogo.svg" className="h-6" />
            </div>
            <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
              {partnerNavLinks.map((link, idx) => (
                <div key={idx} className="w-full">
                  <p
                    key={idx}
                    onClick={() => {
                      handleTabClick(link);
                    }}
                    className={`${currentTab === link?.name && !link.subTabs ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen' : currentTab === link?.name && link.subTabs ? 'text-ctadarkgreen' : 'text-[#858897] hover:bg-gray-100'} flex flex-row justify-between rounded-[6px] p-3 hover:cursor-pointer`}
                  >
                    <span className="flex items-center">
                      {link?.icon}
                      {link?.name}
                    </span>
                    {link.subTabs &&
                      (expandedTab === link.name ? (
                        <CaretUp size={20} />
                      ) : (
                        <CaretDown size={20} />
                      ))}
                  </p>
                  {link.subTabs && expandedTab === link.name && (
                    <div className="mt-2 space-y-2">
                      {link.subTabs.map((sub, subIdx) => (
                        <p
                          key={subIdx}
                          onClick={() => handleSubTabClick(sub.name, sub.path)}
                          className={`flex items-center justify-between rounded-lg p-2 pl-4 ${
                            subTab === sub.name
                              ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen'
                              : 'text-[#858897] hover:bg-gray-100'
                          } hover:cursor-pointer`}
                        >
                          <h2 className="ml-8">{sub.name}</h2>
                          {/* Show notification badge for Transactions */}
                          {sub.notificationCount && (
                            <span className="ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                              {sub.notificationCount}
                            </span>
                          )}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        );
      case Entity.Driver:
        return (
          <div className="flex flex-col items-start gap-y-[12px]">
            <div className="flex w-full justify-start border-b-[1px] border-b-[#E6E6E6] p-[12px] pb-[24px]">
              <img alt="tach-logo" src="/TachLogo.svg" className="h-6" />
            </div>
            <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
              {driverNavLinks.map((link, idx) => (
                <div key={idx} className="w-full">
                  <p
                    key={idx}
                    onClick={() => {
                      handleTabClick(link);
                    }}
                    className={`${currentTab === link?.name && 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen'} flex flex-row justify-between rounded-[6px] p-3 hover:cursor-pointer`}
                  >
                    <span className="flex items-center">
                      {link?.icon}
                      {link?.name}
                    </span>
                  </p>
                </div>
              ))}
            </nav>
          </div>
        );
    }
  };

  const renderUserBox = (userType: Entity) => {
    switch (userType) {
      case Entity.Customer:
        return (
          <div
            onClick={(e: any) => {
              e.stopPropagation();
              togglePopup();
            }}
            className="user-box-toggle relative flex w-fit items-center justify-between space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
              {((userRecord?.userType === Entity.Customer
                ? userRecord?.driver?.first_name
                : null) ?? 'T')[0].toUpperCase()}
            </div>
            {userRecord?.userType === Entity.Customer && (
              <div className="flex flex-col">
                <span className="text-sm text-gray-800">
                  {userRecord?.driver?.first_name +
                    ' ' +
                    userRecord?.driver?.last_name}
                </span>
                <span className="text-xs text-gray-500">
                  {userRecord?.business_name}
                </span>
              </div>
            )}
            <CaretDown
              size={20}
              className={`${popupVisible ? 'rotate-180' : ''}`}
              weight="bold"
            />
            {popupVisible && (
              <div
                ref={popupRef}
                className="absolute left-[0.0625rem] top-full z-20 mt-2 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
              >
                <div>
                  {customerStatus === 'active' && (
                    <button
                      onClick={(e: any) => {
                        e.stopPropagation();
                        navigate('/dashboard/resources');
                        setPopupVisible(false);
                        setShowSideNav(false);
                      }}
                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-gray-700 hover:bg-gray-100"
                    >
                      <Info size={20} />
                      <p>My Resources</p>
                    </button>
                  )}
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      logout();
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                  >
                    <SignOut size={20} />
                    <p>Log Out</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case Entity.Vendor:
        return (
          <div
            onClick={(e: any) => {
              e.stopPropagation();
              togglePopup();
            }}
            className="user-box-toggle relative flex w-fit items-center space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
          >
            {userRecord?.userType === Entity.Vendor &&
            userRecord?.logoUrl &&
            userRecord?.logoUrl?.length > 0 ? (
              <img
                src={userRecord?.logoUrl}
                className="h-8 w-8 rounded-full"
                alt="logo"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                {((userRecord?.userType === Entity.Vendor
                  ? userRecord?.business_name
                  : null) ?? 'T')[0].toUpperCase()}
              </div>
            )}
            {userRecord?.userType === Entity.Vendor && (
              <div className="flex flex-col">
                <span className="text-sm text-gray-800">
                  {userRecord?.business_name}
                </span>
              </div>
            )}
            <CaretDown
              size={20}
              className={`${popupVisible ? 'rotate-180' : ''}`}
              weight="bold"
            />
            {popupVisible && (
              <div
                ref={popupRef}
                className="absolute left-[0.0625rem] right-0 top-[2.85rem] z-10 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl sm:left-auto"
              >
                <div className="flex flex-col">
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setCurrentTab('Profile');
                      navigate('/dashboard/profile');
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                  >
                    <User size={20} />
                    <p>Profile</p>
                  </button>
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      logout();
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                  >
                    <SignOut size={20} />
                    <p>Log Out</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case Entity.Partner:
        return (
          <div
            onClick={(e: any) => {
              e.stopPropagation();
              togglePopup();
            }}
            className="user-box-toggle relative flex w-fit items-center space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
              {((userRecord?.userType === Entity.Partner
                ? userRecord?.business_name
                : null) ?? 'T')[0].toUpperCase()}
            </div>
            {userRecord?.userType === Entity.Partner && (
              <div className="flex flex-col">
                <span className="text-sm text-gray-800">
                  {userRecord?.business_name}
                </span>
              </div>
            )}
            <CaretDown
              size={20}
              className={`${popupVisible ? 'rotate-180' : ''}`}
              weight="bold"
            />
            {popupVisible && (
              <div
                ref={popupRef}
                className="absolute left-[0.0625rem] right-0 top-[2.85rem] z-10 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl sm:left-auto"
              >
                <div className="flex flex-col">
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setCurrentTab('Profile');
                      navigate('/dashboard/profile');
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                  >
                    <User size={20} />
                    <p>Profile</p>
                  </button>
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      logout();
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                  >
                    <SignOut size={20} />
                    <p>Log Out</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case Entity.Driver:
        return (
          <div
            onClick={(e: any) => {
              e.stopPropagation();
              togglePopup();
            }}
            className="user-box-toggle relative flex w-fit items-center justify-between space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
              {((userRecord?.userType === Entity.Driver
                ? userRecord?.first_name
                : null) ?? 'T')[0].toUpperCase()}
            </div>
            {userRecord?.userType === Entity.Driver && (
              <div className="flex flex-col">
                <span className="text-sm text-gray-800">
                  {userRecord?.first_name + ' ' + userRecord?.last_name}
                </span>
                <span className="text-sm text-gray-800">
                  {userRecord?.customer?.business_name}
                </span>
              </div>
            )}
            <CaretDown
              size={20}
              className={`${popupVisible ? 'rotate-180' : ''}`}
              weight="bold"
            />
            {popupVisible && (
              <div
                ref={popupRef}
                className="absolute left-[0.0625rem] right-0 top-[2.85rem] z-10 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
              >
                <div>
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      logout();
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                  >
                    <SignOut size={20} />
                    <p>Log Out</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <>
      {screenView === 'desktop' ? (
        <div className="flex h-screen w-[300px] max-w-[300px] flex-col justify-between overflow-y-auto bg-white p-[16px]">
          {renderNavLinks(userRecord?.userType as Entity)}
          <div className="space-y-2">
            {/* "Promote My Business" moved to the Resources page (2026-07-22,
                Scott). The /dashboard/broker-network route is unchanged — only
                this entry point went away. No replacement CTA in this slot. */}
            {userRecord?.userType === Entity.Customer && !isPreActive && (
              <button
                onClick={() => setIsReferCarrierModalOpen(true)}
                className="flex w-full items-center rounded-[8px] border-[1px] border-green-500 bg-green-50 px-6 py-1.5 hover:bg-green-100"
              >
                <div className="flex flex-row items-center space-x-6">
                  <div className="flex w-6 shrink-0 justify-center">
                    <Gift size={20} className="text-green-600" weight="bold" />
                  </div>
                  <div className="text-left">
                    <p className="text-[14px] font-semibold text-green-900">
                      Refer a Carrier
                    </p>
                    <p className="text-[12px] text-green-700">
                      Earn $200 per referral
                    </p>
                  </div>
                </div>
              </button>
            )}
            <button
              onClick={() => setIsSupportModalOpen(true)}
              className="flex w-full flex-col items-center justify-center space-y-2 rounded-[8px] border-[1px] border-bordergray p-2 hover:bg-gray-50"
            >
              <div className="flex flex-row items-center space-x-4">
                <ChatDots
                  size={20}
                  className="text-graysecondary"
                  weight="bold"
                />
                <p className="text-[16px] text-graytertiary">Contact Support</p>
              </div>
            </button>
          </div>
          <SupportModal
            isOpen={isSupportModalOpen}
            onClose={() => setIsSupportModalOpen(false)}
          />
          {userRecord?.userType === Entity.Customer && (
            <ReferCarrierModal
              isOpen={isReferCarrierModalOpen}
              onClose={() => setIsReferCarrierModalOpen(false)}
            />
          )}
        </div>
      ) : (
        isSideNavVisible && (
          <div
            ref={sideNavRef}
            className={`fixed left-0 top-0 z-[999] flex h-screen w-[300px] max-w-[300px] flex-col overflow-hidden border-r-[1px] border-bordergray bg-white transition-transform duration-300 ease-in-out ${
              isAnimating ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="flex h-[56px] w-full flex-row items-center justify-between border-b-[1px] border-b-[#E6E6E6] p-[12px] px-4">
              <img
                src={logoUrl ?? '/TachLogo.svg'}
                alt={logoUrl ? 'Company logo' : 'Tach Logo'}
                className="h-6 w-auto max-w-[180px] object-contain"
                onError={(e) => {
                  e.currentTarget.src = '/TachLogo.svg';
                }}
              />
              <CaretLeft
                size={20}
                onClick={() => setShowSideNav(false)}
                className="hover:cursor-pointer"
              />
            </div>
            <div className="flex flex-1 flex-col overflow-y-auto p-4">
              <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
                {getEntityNavLinks(userRecord?.userType as Entity)?.map(
                  (link, idx) => (
                    <div key={idx} className="w-full">
                      <p
                        key={idx}
                        onClick={(e: any) => {
                          handleTabClick(link);
                          // setShowSideNav(false);
                        }}
                        className={`${currentTab === link?.name && !link.subTabs ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen' : currentTab === link?.name && link.subTabs ? 'text-ctadarkgreen' : 'text-[#858897] hover:bg-gray-100'} flex flex-row justify-between rounded-[6px] p-3 hover:cursor-pointer`}
                      >
                        <span className="flex items-center">
                          {link?.icon}
                          {link?.name}
                        </span>
                        {link.subTabs &&
                          (expandedTab === link.name ? (
                            <CaretUp size={20} />
                          ) : (
                            <CaretDown size={20} />
                          ))}
                      </p>
                      {link.subTabs && expandedTab === link.name && (
                        <div className="mt-2 space-y-2">
                          {link.subTabs.map((sub, subIdx) => (
                            <p
                              key={subIdx}
                              onClick={() =>
                                handleSubTabClick(sub.name, sub.path)
                              }
                              className={`flex items-center justify-between rounded-lg p-2 pl-4 ${
                                subTab === sub.name
                                  ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen'
                                  : 'text-[#858897] hover:bg-gray-100'
                              } hover:cursor-pointer`}
                            >
                              <h2 className="ml-8">{sub.name}</h2>
                              {/* Show notification badge for Transactions */}
                              {sub.notificationCount && (
                                <span className="ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                                  {sub.notificationCount}
                                </span>
                              )}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                )}
                <div className="flex w-full items-center gap-2">
                  {userRecord?.userType !== Entity.Driver && (
                    <button
                      type="button"
                      aria-label="Settings"
                      className="rounded-full bg-gray-200 p-2 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                      onClick={() => {
                        setCurrentTab('settings');
                        navigate('/dashboard/settings/account');
                        setShowSideNav(false);
                      }}
                    >
                      <Gear size={20} className="text-gray-600" />
                    </button>
                  )}
                  {renderUserBox(userRecord?.userType as Entity)}
                </div>
              </nav>
              <div className="mt-auto space-y-2 pt-4">
                {/* "Promote My Business" moved to the Resources page
                    (2026-07-22, Scott) — see the desktop branch above. */}
                {userRecord?.userType === Entity.Customer && !isPreActive && (
                  <button
                    onClick={() => setIsReferCarrierModalOpen(true)}
                    className="flex w-full items-center rounded-[8px] border-[1px] border-green-500 bg-green-50 px-6 py-1.5 hover:bg-green-100"
                  >
                    <div className="flex flex-row items-center space-x-6">
                      <div className="flex w-6 shrink-0 justify-center">
                        <Gift
                          size={20}
                          className="text-green-600"
                          weight="bold"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-[14px] font-semibold text-green-900">
                          Refer a Carrier
                        </p>
                        <p className="text-[12px] text-green-700">
                          Earn $200 per referral
                        </p>
                      </div>
                    </div>
                  </button>
                )}
                <button
                  onClick={() => setIsSupportModalOpen(true)}
                  className="flex w-full flex-col items-center justify-center space-y-2 rounded-[8px] border-[1px] border-bordergray p-2 hover:bg-gray-50"
                >
                  <div className="flex flex-row items-center space-x-4">
                    <ChatDots
                      size={20}
                      className="text-graysecondary"
                      weight="bold"
                    />
                    <p className="text-[16px] text-graytertiary">
                      Contact Support
                    </p>
                  </div>
                </button>
              </div>
            </div>
            <SupportModal
              isOpen={isSupportModalOpen}
              onClose={() => setIsSupportModalOpen(false)}
            />
            <ReferCarrierModal
              isOpen={isReferCarrierModalOpen}
              onClose={() => setIsReferCarrierModalOpen(false)}
            />
          </div>
        )
      )}
    </>
  );
};

export default SideNav;

export const HelpBox = () => {
  return (
    <div className="flex w-full flex-col items-center justify-center space-y-2 rounded-[8px] border-[1px] border-accent p-1 text-accent">
      <div className="flex w-full flex-row items-center space-x-1 text-[18px] font-[600]">
        <ChatCenteredDots size={20} weight="bold" />
        <p>Contact Support</p>
      </div>
      <a
        href="tel:+18668728224"
        className="flex w-full flex-row items-center space-x-1 font-[500] text-primary hover:underline"
      >
        <Phone size={20} />
        <p>+1 (866) USA-TACH</p>
      </a>
      <a
        href="mailto:support@tachusa.com"
        className="flex w-full flex-row items-center space-x-1 font-[500] text-primary hover:underline"
      >
        <Envelope size={20} />
        <p>support@tachusa.com</p>
      </a>
    </div>
  );
};

export const EmergencyHelp = () => {
  return (
    <button className="flex w-full flex-row items-center justify-center space-x-2 rounded-[8px] border-[1px] border-danger p-1 text-danger hover:bg-red-200">
      <PhoneOutgoing size={30} weight="bold" />
      <p>Get Emergency Help</p>
    </button>
  );
};
